// Sync-issues / docs-health view (RFC-0009 Task 3.2, §3/§6; PD-0006).
//
// Two blocks, both derived from RFC-0006's read endpoints (this RFC adds no API
// surface): a stat-tile row (four counts from `GET /api/projects/summary` plus a
// manifest-adoption % derived from the full list) and a filterable issue list
// (one row per issue per repo, most-recent-first). Renders the §6 states —
// skeleton, envelope-aware error panel, and the explicit "all healthy" empty
// state (tiles still render their zero counts, never a blank table).

import { useSearchParams, Link } from 'react-router-dom';
import {
  Books,
  WarningOctagon,
  ArrowsClockwise,
  Clock,
  SealCheck,
  Heartbeat,
  CaretRight,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { StatCard } from '@/components/ui/stat-card';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { StatTilesSkeleton, ListRowsSkeleton } from '@/components/ui/query-skeletons';
import { ErrorPanel } from '@/components/ui/error-panel';
import { useProjectsHealthSummary } from '@/lib/queries/projects';
import {
  useSyncIssues,
  useManifestAdoption,
  type SyncIssueKind,
} from '@/lib/queries/sync-issues';

const KIND_META: Record<SyncIssueKind, { label: string; variant: BadgeVariant }> = {
  broken_manifest: { label: 'Broken manifest', variant: 'error' },
  failed_sync: { label: 'Failed sync', variant: 'error' },
  stale: { label: 'Stale', variant: 'warning' },
  auto_indexed: { label: 'Auto-indexed', variant: 'ghost' },
};

const KIND_FILTERS: { value: SyncIssueKind | 'all'; label: string }[] = [
  { value: 'all', label: 'All issues' },
  { value: 'broken_manifest', label: 'Broken manifest' },
  { value: 'failed_sync', label: 'Failed sync' },
  { value: 'stale', label: 'Stale' },
  { value: 'auto_indexed', label: 'Auto-indexed' },
];

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 1000 * 60 * 60 * 24 * 365],
  ['month', 1000 * 60 * 60 * 24 * 30],
  ['day', 1000 * 60 * 60 * 24],
  ['hour', 1000 * 60 * 60],
  ['minute', 1000 * 60],
];

/** Relative-format an ISO timestamp (e.g. "3 days ago"); em-dash when absent. */
function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  const diff = ms - Date.now();
  for (const [unit, span] of UNITS) {
    if (Math.abs(diff) >= span) {
      return RELATIVE.format(Math.round(diff / span), unit);
    }
  }
  return RELATIVE.format(Math.round(diff / 1000), 'second');
}

function isKind(value: string | null): value is SyncIssueKind {
  return value !== null && value in KIND_META;
}

export function SyncIssuesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const kindParam = searchParams.get('kind');
  const kind = isKind(kindParam) ? kindParam : undefined;

  const summary = useProjectsHealthSummary();
  const adoption = useManifestAdoption();
  const issues = useSyncIssues({ kind });

  const rows = issues.data ?? [];

  function selectKind(value: SyncIssueKind | 'all') {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('kind');
    else next.set('kind', value);
    setSearchParams(next);
  }

  const adoptionLabel =
    adoption.data && adoption.data.adoptionPct !== null
      ? `${adoption.data.adoptionPct}%`
      : '—';

  return (
    <>
      <PageHeader
        title="Sync issues"
        description="Repos with broken manifests, failing or stale syncs, and auto-indexed (unregistered) status — plus manifest adoption across the portal."
      />

      {/* Stat tiles — four health counts + manifest adoption. */}
      <section className="mb-8">
        {summary.isLoading ? (
          <StatTilesSkeleton count={5} />
        ) : summary.isError ? (
          <ErrorPanel error={summary.error} onRetry={() => void summary.refetch()} />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <StatCard
              label="Repositories"
              value={summary.data?.projects ?? 0}
              icon={<Books size={16} weight="duotone" />}
            />
            <StatCard
              label="Manifest broken"
              value={summary.data?.manifestBroken ?? 0}
              icon={<WarningOctagon size={16} weight="duotone" />}
            />
            <StatCard
              label="Sync failed"
              value={summary.data?.syncFailed ?? 0}
              icon={<ArrowsClockwise size={16} weight="duotone" />}
            />
            <StatCard
              label="Stale"
              value={summary.data?.stale ?? 0}
              icon={<Clock size={16} weight="duotone" />}
            />
            <StatCard
              label="Manifest adoption"
              value={adoptionLabel}
              icon={<SealCheck size={16} weight="duotone" />}
            />
          </div>
        )}
      </section>

      {/* Kind filter — segmented control (default: all kinds). */}
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {KIND_FILTERS.map((f) => {
          const active = f.value === 'all' ? kind === undefined : kind === f.value;
          return (
            <button
              key={f.value}
              type="button"
              aria-pressed={active}
              onClick={() => selectKind(f.value)}
              className={[
                'inline-flex min-h-[44px] items-center rounded-full border px-3.5 py-2 text-xs font-medium transition-colors',
                active
                  ? 'border-accent bg-accent-faint text-text'
                  : 'border-border text-muted hover:text-text',
              ].join(' ')}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Issue list. */}
      {issues.isLoading ? (
        <ListRowsSkeleton rows={6} />
      ) : issues.isError ? (
        <ErrorPanel error={issues.error} onRetry={() => void issues.refetch()} />
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-border bg-surface shadow-[var(--c-shadow-sm)]">
          <EmptyState
            icon={<Heartbeat size={22} />}
            title="All healthy"
            description={
              kind
                ? 'No repositories match this issue kind. Try another filter.'
                : 'No repositories have sync issues right now — manifests are valid, syncs are current, and everything is registered.'
            }
          />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-[var(--c-shadow-sm)]">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-border text-[11px] font-semibold uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">Project</th>
                <th className="px-4 py-3 font-semibold">Issue kind</th>
                <th className="px-4 py-3 font-semibold">Detail</th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">Since / last synced</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((row) => {
                const meta = KIND_META[row.kind];
                return (
                  <tr key={`${row.repoId}:${row.kind}`} className="hover:bg-accent-faint/40">
                    <td className="px-4 py-3">
                      <Link
                        to={`/repositories/${encodeURIComponent(row.repoName)}`}
                        className="group inline-flex items-center gap-1 font-medium text-text hover:text-accent"
                      >
                        {row.repoName}
                        <CaretRight
                          size={12}
                          weight="bold"
                          className="text-text/30 transition-colors group-hover:text-accent"
                        />
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={meta.variant}>{meta.label}</Badge>
                    </td>
                    <td className="px-4 py-3 text-muted">
                      {row.detail ? (
                        <span className="line-clamp-2 font-mono text-[12px]">{row.detail}</span>
                      ) : (
                        <span className="text-muted/60">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted">
                      {formatRelative(row.since)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
